import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { DBAProvider } from '@/contexts/DBAContext';
import { Toaster } from '@/components/ui/sonner';
import Layout from '@/components/Layout';
import LoginPage from '@/pages/LoginPage';
import DashboardPage from '@/pages/DashboardPage';
import LeadsPage from '@/pages/LeadsPage';
import CustomersPage from '@/pages/CustomersPage';
import CustomerDetailPage from '@/pages/CustomerDetailPage';
import JobsPage from '@/pages/JobsPage';
import InvoicesPage from '@/pages/InvoicesPage';
import QuotesPage from '@/pages/QuotesPage';
import TeamPage from '@/pages/TeamPage';
import ReportsPage from '@/pages/ReportsPage';
import StaffView from '@/pages/StaffView';
import PublicLeadForm from '@/pages/PublicLeadForm';
import PublicQuoteView from '@/pages/PublicQuoteView';
import CustomerPortal from '@/pages/CustomerPortal';
import PublicBookingForm from '@/pages/PublicBookingForm';
import DBAManagementPage from '@/pages/DBAManagementPage';
import SettingsPage from '@/pages/SettingsPage';
import ApiKeysPage from '@/pages/ApiKeysPage';
import CommunicationsPage from '@/pages/CommunicationsPage';
import ExpensesReviewPage from '@/pages/ExpensesReviewPage';
import OperatorManualPage from '@/pages/OperatorManualPage';
import LeadMonitoringPage from '@/pages/LeadMonitoringPage';
import ProofPacketPage from '@/pages/ProofPacketPage';
import MileagePage from '@/pages/MileagePage';
import FleetPage from '@/pages/FleetPage';
import DisposalNetworkPage from '@/pages/DisposalNetworkPage';
import DesignSamplesPage from '@/pages/DesignSamplesPage';
import EstimatorPage from '@/pages/EstimatorPage';
import MobileEstimatorPage from '@/pages/MobileEstimatorPage';
import PWAInstallPrompt from '@/components/PWAInstallPrompt';
import { useIsMobile } from '@/hooks/useIsMobile';

const routeByResource = {
  dashboard: '/',
  leads: '/leads',
  customers: '/customers',
  jobs: '/jobs',
  quotes: '/quotes',
  estimator: '/estimator',
  invoices: '/invoices',
  communications: '/comms',
  team: '/team',
  reports: '/reports',
  monitoring: '/lead-monitoring',
  mileage: '/mileage',
  fleet: '/fleet',
  disposal: '/disposal',
  expenses: '/expenses',
};

function defaultPathForUser(user) {
  if (!user) return '/login';
  if (user.role === 'field_staff') return '/staff';
  if (user.role === 'admin') return '/';
  const permissions = user.permissions || {};
  const firstResource = Object.keys(routeByResource).find((resource) => permissions[resource]?.length > 0);
  return firstResource ? routeByResource[firstResource] : '/staff';
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-100 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-zinc-300 border-t-zinc-900 rounded-full animate-spin" />
      </div>
    );
  }

  if (user === false) {
    return <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />;
  }

  return children;
}

function AdminRoute({ children }) {
  const { user } = useAuth();
  if (user && user.role !== 'admin') {
    return <Navigate to="/" replace />;
  }
  return children;
}

function StaffOnlyRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-100 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-zinc-300 border-t-zinc-900 rounded-full animate-spin" />
      </div>
    );
  }

  if (user === false) {
    return <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />;
  }

  if (user?.role !== 'field_staff') {
    return <Navigate to={defaultPathForUser(user)} replace />;
  }

  return children;
}

function HomeRoute() {
  const { user } = useAuth();
  if (user?.role === 'admin' || user?.permissions?.dashboard?.length > 0) return <DashboardPage />;
  return <Navigate to={defaultPathForUser(user)} replace />;
}

function PermissionRoute({ resource, children }) {
  const { user } = useAuth();
  if (user?.role === 'admin') return children;
  if (user?.permissions?.[resource]?.length > 0) return children;
  return <Navigate to="/" replace />;
}

function AuthRedirect({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-zinc-700 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  if (user) {
    const target = location.state?.from;
    if (target && typeof target === 'string' && target.startsWith('/')) {
      return <Navigate to={target} replace />;
    }
    return <Navigate to={defaultPathForUser(user)} replace />;
  }

  return children;
}

function MobileDeviceRedirect() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const isMobile = useIsMobile();
  const technicianPath = location.pathname.startsWith('/staff') || location.pathname.startsWith('/mobile-estimator');

  if (loading || !user || user === false || !isMobile || technicianPath) return null;
  if (user.role === 'field_staff') {
    return <Navigate to="/staff" replace state={{ mobileAutoRedirect: true }} />;
  }
  return null;
}

function AppRoutes() {
  const { user } = useAuth();

  return (
    <>
      <MobileDeviceRedirect />
      <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          <AuthRedirect>
            <LoginPage />
          </AuthRedirect>
        }
      />
      <Route path="/intake/:slug" element={<PublicLeadForm />} />
      <Route path="/quote/:quoteId" element={<PublicQuoteView />} />
      <Route path="/portal/:invoiceId" element={<CustomerPortal />} />
      <Route path="/proof/:token" element={<ProofPacketPage />} />
      <Route path="/book/:slug" element={<PublicBookingForm />} />

      {/* Staff view */}
      <Route
        path="/staff"
        element={
          <StaffOnlyRoute>
            <StaffView />
          </StaffOnlyRoute>
        }
      />
      <Route
        path="/mobile-estimator"
        element={
          <ProtectedRoute>
            <DBAProvider>
              <PermissionRoute resource="estimator"><MobileEstimatorPage /></PermissionRoute>
            </DBAProvider>
          </ProtectedRoute>
        }
      />
      {/* Main app routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DBAProvider>
              {user?.role === 'field_staff' ? <Navigate to="/staff" replace /> : <Layout />}
            </DBAProvider>
          </ProtectedRoute>
        }
      >
        <Route index element={<HomeRoute />} />
        <Route path="leads" element={<PermissionRoute resource="leads"><LeadsPage /></PermissionRoute>} />
        <Route path="customers" element={<PermissionRoute resource="customers"><CustomersPage /></PermissionRoute>} />
        <Route path="customers/:customerId" element={<PermissionRoute resource="customers"><CustomerDetailPage /></PermissionRoute>} />
        <Route path="jobs" element={<PermissionRoute resource="jobs"><JobsPage /></PermissionRoute>} />
        <Route path="quotes" element={<PermissionRoute resource="quotes"><QuotesPage /></PermissionRoute>} />
        <Route path="estimator" element={<PermissionRoute resource="estimator"><EstimatorPage /></PermissionRoute>} />
        <Route path="invoices" element={<PermissionRoute resource="invoices"><InvoicesPage /></PermissionRoute>} />
        <Route path="comms" element={<PermissionRoute resource="communications"><CommunicationsPage /></PermissionRoute>} />
        <Route path="team" element={<PermissionRoute resource="team"><TeamPage /></PermissionRoute>} />
        <Route path="reports" element={<PermissionRoute resource="reports"><ReportsPage /></PermissionRoute>} />
        <Route path="lead-monitoring" element={<PermissionRoute resource="monitoring"><LeadMonitoringPage /></PermissionRoute>} />
        <Route path="mileage" element={<PermissionRoute resource="mileage"><MileagePage /></PermissionRoute>} />
        <Route path="fleet" element={<PermissionRoute resource="fleet"><FleetPage /></PermissionRoute>} />
        <Route path="disposal" element={<PermissionRoute resource="disposal"><DisposalNetworkPage /></PermissionRoute>} />
        <Route path="expenses" element={<PermissionRoute resource="expenses"><ExpensesReviewPage /></PermissionRoute>} />
        <Route path="dbas" element={<PermissionRoute resource="businesses"><DBAManagementPage /></PermissionRoute>} />
        <Route path="businesses" element={<Navigate to="/dbas" replace />} />
        <Route path="manual" element={<PermissionRoute resource="manual"><OperatorManualPage /></PermissionRoute>} />
        <Route path="integrations" element={<PermissionRoute resource="integrations"><ApiKeysPage /></PermissionRoute>} />
        <Route path="api-keys" element={<Navigate to="/integrations" replace />} />
        <Route path="settings" element={<PermissionRoute resource="settings"><SettingsPage /></PermissionRoute>} />
        <Route path="design-samples" element={<PermissionRoute resource="settings"><DesignSamplesPage /></PermissionRoute>} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <PWAInstallPrompt />
        <Toaster />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
